import { useEffect, useState, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable, type Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Warehouse } from '@/types/warehouse.types';
import { Building2, Star, MapPin, Phone } from 'lucide-react';

interface TenantWarehousesListProps {
  tenantId: string;
}

export function TenantWarehousesList({ tenantId }: TenantWarehousesListProps) {
  const { get, loading } = useApi();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  const fetchWarehouses = useCallback(async () => {
    if (!tenantId) return;
    
    try {
      const response: any = await get(`/api/v1/dashboard/tenants/${tenantId}/warehouses`);
      if (response?.data && Array.isArray(response.data)) {
        setWarehouses(response.data);
      } else if (Array.isArray(response)) {
        setWarehouses(response);
      } else {
        setWarehouses([]);
      }
    } catch (error) {
      console.error('Failed to fetch tenant warehouses:', error);
      setWarehouses([]);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const columns: Column<Warehouse>[] = [
    {
      header: 'الرمز',
      accessor: (row) => (
        <span className="font-mono text-sm">{row.code}</span>
      ),
    },
    {
      header: 'الاسم',
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
          {row.is_default && (
            <Badge variant="secondary" className="gap-1">
              <Star className="h-3 w-3 fill-current" />
              افتراضي
            </Badge>
          )}
        </div>
      ),
    },
    {
      header: 'النوع',
      accessor: (row) => (
        <Badge variant="outline">
          {row.type === 'main' ? 'رئيسي' : 'فرعي'}
        </Badge>
      ),
    },
    {
      header: 'المدينة',
      accessor: (row) => row.city_name?.ar || '-',
    },
    {
      header: 'العنوان',
      accessor: (row) => (
        <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
          {row.address}
        </span>
      ),
    },
    {
      header: 'الحالة',
      accessor: (row) => (
        <Badge variant={row.is_active ? 'default' : 'secondary'}>
          {row.is_active ? 'نشط' : 'غير نشط'}
        </Badge>
      ),
    },
  ];

  if (loading && warehouses.length === 0) {
    return <LoadingSkeleton type="table" rows={5} />;
  }

  if (warehouses.length === 0) {
    return (
      <EmptyState
        icon={<Building2 className="h-12 w-12" />}
        title="لا توجد مستودعات"
        description="لم يتم إضافة أي مستودعات لهذا الشريك بعد"
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <DataTable
          data={warehouses}
          columns={columns}
          emptyMessage="لا توجد مستودعات"
        />
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {warehouses.map((warehouse) => (
          <Card key={warehouse.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{warehouse.name}</h3>
                      {warehouse.is_default && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          <Star className="h-3 w-3 fill-current" />
                          افتراضي
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">{warehouse.code}</p>
                  </div>
                </div>
                <Badge variant={warehouse.is_active ? 'default' : 'secondary'}>
                  {warehouse.is_active ? 'نشط' : 'غير نشط'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>{warehouse.city_name?.ar || '-'}</span>
                </div>
                {warehouse.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />
                    <span dir="ltr">{warehouse.phone}</span>
                  </div>
                )}
              </div>

              <div className="text-xs text-muted-foreground pt-2 border-t truncate">
                {warehouse.address}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
